import { useEffect, useState } from "react";

/**
 * App-wide list-search debounce + minimum-length gate.
 *
 * Returns the "applied" keyword derived from the raw controlled-input value
 * `searchText`: it updates only ~`delay`ms after the user stops typing, and only
 * once at least `minChars` characters are present (fewer characters = empty =
 * show all). Wire the RETURNED value into the fetch effect's dependency array and
 * into the request URL (in place of the raw searchText) so a list fetches once
 * the user pauses — not on every keystroke. Mirrors the Scheme-Verification UX.
 *
 *   const [searchText, setSearchText] = useState("");
 *   const appliedSearch = useDebouncedSearch(searchText);
 *   // input stays bound to searchText/setSearchText (responsive)
 *   useEffect(() => { getList(currentPageNo); }, [appliedSearch, currentPageNo]);
 *   // URL: `...&keyword=${encodeURIComponent(appliedSearch)}`
 */
export function useDebouncedSearch(
  searchText: any,
  minChars: number = 3,
  delay: number = 800
): string {
  const [applied, setApplied] = useState<string>("");
  useEffect(() => {
    const timer = setTimeout(() => {
      const raw = (searchText ?? "").toString().trim();
      setApplied(raw.length >= minChars ? raw : "");
    }, delay);
    return () => clearTimeout(timer);
  }, [searchText, minChars, delay]);
  return applied;
}

export default useDebouncedSearch;
